import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Image as ImageIcon, Upload, X, ZoomIn, Sparkles, Camera, FolderHeart, Video, Mic, Play, Pause, Trash2, ExternalLink, Palette, MoreVertical } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import ImageLightbox from '../components/ImageLightbox';
import ImageCropModal from '../components/ImageCropModal';
import MediaRecorderModal from '../components/MediaRecorder';
import { SimsConfirmDialog } from '../components/SimsDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fun Sims-style Plumbob
const Plumbob = ({ color = 'purple', size = 'md' }) => {
  const sizes = { sm: 'w-4 h-4', md: 'w-6 h-6', lg: 'w-8 h-8' };
  const colors = {
    green: ['#4ade80', '#22c55e', '#16a34a'],
    purple: ['#3A5C8F', '#2B4C7E', '#1E3A63'],
    pink: ['#D0B384', '#C8A97E', '#B3956B'],
    blue: ['#60a5fa', '#3b82f6', '#2563eb']
  };
  const [c1, c2, c3] = colors[color] || colors.purple;
  
  return (
    <motion.svg
      viewBox="0 0 24 24"
      className={sizes[size]}
      animate={{ rotateY: [0, 180, 360] }}
      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
    >
      <defs>
        <linearGradient id={`plumbob-albums-${color}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={c1} />
          <stop offset="50%" stopColor={c2} />
          <stop offset="100%" stopColor={c3} />
        </linearGradient>
      </defs>
      <polygon
        points="12,2 22,12 12,22 2,12"
        fill={`url(#plumbob-albums-${color})`}
        stroke={c3}
        strokeWidth="1"
      />
    </motion.svg>
  );
};

export default function Albums() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [newAlbum, setNewAlbum] = useState({ title: '' });
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  
  // Crop modal state
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [pendingUpload, setPendingUpload] = useState({ albumId: null, files: [] });
  
  // Media recorder state
  const [recorderOpen, setRecorderOpen] = useState(false);
  const [recorderType, setRecorderType] = useState('video');
  const [recordingAlbumId, setRecordingAlbumId] = useState(null);
  
  // Video/Audio playback state
  const [playingMedia, setPlayingMedia] = useState(null);
  
  // Delete album state
  const [deleteAlbumDialog, setDeleteAlbumDialog] = useState(false);
  const [albumToDelete, setAlbumToDelete] = useState(null);

  useEffect(() => {
    loadAlbums();
  }, []);

  const loadAlbums = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      navigate('/');
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [albumsRes, userRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/albums`, { headers }),
        axios.get(`${BACKEND_URL}/api/auth/me`, { headers })
      ]);
      setAlbums(albumsRes.data);
      setUser(userRes.data);
    } catch (error) {
      console.error('Failed to load albums:', error);
      toast.error('Failed to load albums');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoSelect = (albumId, files) => {
    if (!files || files.length === 0) return;
    
    // Convert FileList to Array and upload all files directly without cropping
    const filesArray = Array.from(files);
    handlePhotoUpload(albumId, filesArray);
  };

  // Handle video upload from device
  const handleVideoUpload = async (albumId, files) => {
    if (!files || files.length === 0) return;

    const token = localStorage.getItem('session_token');
    setUploadingPhotos(true);

    try {
      const formData = new FormData();
      const filesArray = Array.from(files);
      for (let file of filesArray) {
        formData.append('files', file);
      }

      await axios.post(
        `${BACKEND_URL}/api/albums/${albumId}/videos`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success(`🎬 ${filesArray.length} video${filesArray.length > 1 ? 's' : ''} uploaded!`);
      loadAlbums();
    } catch (error) {
      console.error('Failed to upload videos:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload videos');
    } finally {
      setUploadingPhotos(false);
    }
  };

  // Request camera/video permissions
  const requestCameraPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      stream.getTracks().forEach(track => track.stop());
      toast.success('📹 Camera and microphone access granted!');
    } catch (error) {
      console.error('Permission denied:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Permission denied. Please enable camera access in your browser settings.');
        // Try to open browser settings (works on some browsers)
        if (navigator.permissions && navigator.permissions.query) {
          toast.info('Go to your browser settings to enable camera permissions.');
        }
      } else {
        toast.error('Could not access camera. Please check your device.');
      }
    }
  };

  const handleCropComplete = async (croppedBlob) => {
    const { albumId } = pendingUpload;
    
    const token = localStorage.getItem('session_token');
    setUploadingPhotos(true);

    try {
      const formData = new FormData();
      formData.append('files', croppedBlob, 'photo.jpg');

      await axios.post(
        `${BACKEND_URL}/api/albums/${albumId}/photos`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success('🎉 Photo added to album!');
      loadAlbums();
    } catch (error) {
      console.error('Failed to upload photo:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhotos(false);
      if (imageToCrop) {
        URL.revokeObjectURL(imageToCrop);
        setImageToCrop(null);
      }
      setPendingUpload({ albumId: null, files: [] });
    }
  };

  const handlePhotoUpload = async (albumId, files) => {
    if (!files || files.length === 0) return;

    const token = localStorage.getItem('session_token');
    setUploadingPhotos(true);

    try {
      const formData = new FormData();
      const filesArray = Array.isArray(files) ? files : Array.from(files);
      for (let file of filesArray) {
        formData.append('files', file);
      }

      await axios.post(
        `${BACKEND_URL}/api/albums/${albumId}/photos`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success(`✨ ${filesArray.length} photo${filesArray.length > 1 ? 's' : ''} uploaded!`);
      loadAlbums();
    } catch (error) {
      console.error('Failed to upload photos:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload photos');
    } finally {
      setUploadingPhotos(false);
    }
  };

  const deletePhoto = async (albumId, photoIndex) => {
    const token = localStorage.getItem('session_token');

    try {
      await axios.delete(
        `${BACKEND_URL}/api/albums/${albumId}/photos/${photoIndex}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Photo removed');
      loadAlbums();
    } catch (error) {
      console.error('Failed to delete photo:', error);
      toast.error('Failed to delete photo');
    }
  };

  // Open media recorder
  const openRecorder = (albumId, type) => {
    setRecordingAlbumId(albumId);
    setRecorderType(type);
    setRecorderOpen(true);
  };

  // Handle recorded media save
  const handleRecordingSave = async (blob, type) => {
    if (!recordingAlbumId || !blob) return;
    
    const token = localStorage.getItem('session_token');
    setUploadingPhotos(true);
    
    try {
      const formData = new FormData();
      const extension = type === 'video' ? 'webm' : 'webm';
      const filename = `${type}_${Date.now()}.${extension}`;
      formData.append('files', blob, filename);
      
      const endpoint = type === 'video' 
        ? `${BACKEND_URL}/api/albums/${recordingAlbumId}/videos`
        : `${BACKEND_URL}/api/albums/${recordingAlbumId}/audio`;
      
      await axios.post(endpoint, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(`🎉 ${type === 'video' ? 'Video' : 'Voice recording'} added!`);
      loadAlbums();
    } catch (error) {
      console.error(`Failed to upload ${type}:`, error);
      toast.error(`Failed to upload ${type}`);
    } finally {
      setUploadingPhotos(false);
      setRecordingAlbumId(null);
    }
  };

  // Delete video
  const deleteVideo = async (albumId, videoIndex) => {
    const token = localStorage.getItem('session_token');
    
    try {
      await axios.delete(
        `${BACKEND_URL}/api/albums/${albumId}/videos/${videoIndex}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Video removed');
      loadAlbums();
    } catch (error) {
      console.error('Failed to delete video:', error);
      toast.error('Failed to delete video');
    }
  };

  // Delete audio
  const deleteAudio = async (albumId, audioIndex) => {
    const token = localStorage.getItem('session_token');
    
    try {
      await axios.delete(
        `${BACKEND_URL}/api/albums/${albumId}/audio/${audioIndex}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Audio removed');
      loadAlbums();
    } catch (error) {
      console.error('Failed to delete audio:', error);
      toast.error('Failed to delete audio');
    }
  };

  const createAlbum = async () => {
    if (!newAlbum.title) {
      toast.error('Album title is required');
      return;
    }

    const token = localStorage.getItem('session_token');
    setIsCreating(true);

    try {
      await axios.post(
        `${BACKEND_URL}/api/albums`,
        newAlbum,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('🎉 Album created!');
      setNewAlbum({ title: '' });
      setCreateDialogOpen(false);
      loadAlbums();
    } catch (error) {
      console.error('Failed to create album:', error);
      toast.error('Failed to create album');
    } finally {
      setIsCreating(false);
    }
  };

  const openLightbox = (imageUrl) => {
    setSelectedImage(imageUrl);
    setLightboxOpen(true);
  };

  // Delete album
  const deleteAlbum = async () => {
    if (!albumToDelete) return;
    
    const token = localStorage.getItem('session_token');
    try {
      await axios.delete(
        `${BACKEND_URL}/api/albums/${albumToDelete.album_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Album deleted');
      setDeleteAlbumDialog(false);
      setAlbumToDelete(null);
      loadAlbums();
    } catch (error) {
      console.error('Failed to delete album:', error);
      toast.error('Failed to delete album');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#EBF0F5] via-[#F7F1E8] to-[#EBF0F5]">
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="flex justify-center mb-4">
            <Plumbob color="pink" size="lg" />
          </div>
          <p className="text-gray-600 font-medium">Loading your memories...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#EBF0F5] via-[#F7F1E8] to-[#EBF0F5]" data-testid="albums-page">
      <PageHeader 
        title="Memory Albums" 
        user={user}
        titleIcon={<Plumbob color="pink" size="md" />}
        actions={
          <div className="flex items-center gap-2">
            <Button 
              onClick={requestCameraPermission}
              variant="outline"
              className="rounded-full border-[#D7E0EA] hover:bg-[#EBF0F5] text-xs sm:text-sm px-2 sm:px-3"
              title="Enable camera permissions"
            >
              <Video className="h-4 w-4 sm:mr-1 text-[#2B4C7E]" />
              <span className="hidden sm:inline">Permissions</span>
            </Button>
            <Button 
              onClick={() => setCreateDialogOpen(true)}
              className="bg-gradient-to-r from-[#2B4C7E] to-[#C8A97E] text-white rounded-full hover:from-[#2B4C7E] hover:to-[#B3956B] shadow-lg shadow-[#D7E0EA] text-xs sm:text-sm px-3 sm:px-6 py-5 sm:py-6" 
              data-testid="create-album-btn"
            >
              <Plus className="h-4 w-4 sm:h-5 sm:w-5 sm:mr-2" />
              <span className="font-semibold hidden sm:inline">Create Album</span>
              <span className="sm:hidden">New</span>
            </Button>
          </div>
        }
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-4 md:p-8">
        {albums.length === 0 ? (
          <motion.div 
            className="text-center py-24"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="w-24 h-24 bg-gradient-to-br from-[#EBF0F5] to-[#F2E9D9] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <FolderHeart className="h-12 w-12 text-[#2B4C7E]" strokeWidth={1.5} />
            </div>
            <h3 className="text-2xl font-bold bg-gradient-to-r from-[#2B4C7E] to-[#B3956B] bg-clip-text text-transparent mb-3">
              No albums yet!
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Create albums to organize precious photos and videos for your loved ones. They&apos;ll treasure these memories forever.
            </p>
            <Button 
              onClick={() => setCreateDialogOpen(true)}
              className="bg-gradient-to-r from-[#2B4C7E] to-[#C8A97E] text-white rounded-full shadow-lg"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create Your First Album
            </Button>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence>
              {albums.map((album, index) => (
                <motion.div
                  key={album.album_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="rounded-3xl border-2 border-[#EBF0F5] shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 bg-white/80 backdrop-blur-sm overflow-hidden">
                    {/* Album Preview */}
                    <div className="aspect-video bg-gradient-to-br from-[#EBF0F5] to-[#F7F1E8] relative overflow-hidden">
                      {album.photos && album.photos.length > 0 ? (
                        <div className="grid grid-cols-2 gap-1 w-full h-full">
                          {album.photos.slice(0, 4).map((photo, photoIndex) => (
                            <div 
                              key={photoIndex} 
                              className="relative group cursor-pointer overflow-hidden"
                              onClick={() => openLightbox(photo)}
                            >
                              <img
                                src={photo}
                                alt={`Album ${album.title} photo ${photoIndex + 1}`}
                                className="w-full h-full object-cover transition-transform group-hover:scale-110"
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                                <ZoomIn className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                            </div>
                          ))}
                          {album.photos.length > 4 && (
                            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end justify-center pb-4">
                              <span className="text-white font-bold text-lg">+{album.photos.length - 4} more</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="h-16 w-16 text-[#B9C7D6]" strokeWidth={1} />
                        </div>
                      )}
                    </div>

                    {/* Album Info */}
                    <div className="p-5">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-bold bg-gradient-to-r from-[#2B4C7E] to-[#B3956B] bg-clip-text text-transparent">
                          {album.title}
                        </h3>
                        <div className="flex items-center gap-1 sm:gap-2 flex-wrap justify-end">
                          {/* Photo upload - multiple */}
                          <input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={(e) => handlePhotoSelect(album.album_id, e.target.files)}
                            className="hidden"
                            id={`upload-${album.album_id}`}
                          />
                          <label
                            htmlFor={`upload-${album.album_id}`}
                            className="cursor-pointer p-2 rounded-full bg-gradient-to-r from-[#EBF0F5] to-[#F2E9D9] hover:from-[#D7E0EA] hover:to-[#EAD9BF] transition-colors flex items-center justify-center"
                            title="Add photos (multiple)"
                          >
                            <Camera className="h-4 w-4 text-[#2B4C7E]" />
                          </label>
                          
                          {/* Video upload from device */}
                          <input
                            type="file"
                            multiple
                            accept="video/*"
                            onChange={(e) => handleVideoUpload(album.album_id, e.target.files)}
                            className="hidden"
                            id={`upload-video-${album.album_id}`}
                          />
                          <label
                            htmlFor={`upload-video-${album.album_id}`}
                            className="cursor-pointer p-2 rounded-full bg-gradient-to-r from-[#F2E9D9] to-[#F2E9D9] hover:from-[#EAD9BF] hover:to-[#EAD9BF] transition-colors flex items-center justify-center"
                            title="Upload videos (multiple)"
                          >
                            <Video className="h-4 w-4 text-[#B3956B]" />
                          </label>
                          
                          {/* Video record button removed — record-in-app disabled */}
                          
                          {/* Audio record button */}
                          <button
                            onClick={() => openRecorder(album.album_id, 'audio')}
                            className="p-2 rounded-full bg-gradient-to-r from-[#EBF0F5] to-[#EBF0F5] hover:from-[#D7E0EA] hover:to-[#D7E0EA] transition-colors flex items-center justify-center"
                            title="Record voice message"
                          >
                            <Mic className="h-4 w-4 text-[#2B4C7E]" />
                          </button>
                          
                          {/* Album menu (delete) */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="p-2 rounded-full hover:bg-gray-100 transition-colors">
                                <MoreVertical className="h-4 w-4 text-gray-500" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem 
                                onClick={() => {
                                  setAlbumToDelete(album);
                                  setDeleteAlbumDialog(true);
                                }}
                                className="text-red-600 focus:text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete Album
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                      
                      {/* Media counts */}
                      <div className="flex items-center gap-3 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <ImageIcon className="h-4 w-4" />
                          {album.photos?.length || 0}
                        </span>
                        <span className="flex items-center gap-1">
                          <Video className="h-4 w-4" />
                          {album.videos?.length || 0}
                        </span>
                        <span className="flex items-center gap-1">
                          <Mic className="h-4 w-4" />
                          {album.audio_recordings?.length || 0}
                        </span>
                      </div>

                      {/* Photo Thumbnails */}
                      {album.photos && album.photos.length > 0 && (
                        <div className="mt-4">
                          <p className="text-xs text-gray-400 mb-2 font-medium">Photos</p>
                          <div className="flex flex-wrap gap-2">
                            {album.photos.slice(0, 6).map((photo, photoIndex) => (
                              <div key={photoIndex} className="relative group">
                                <img
                                  src={photo}
                                  alt={`Photo ${photoIndex + 1}`}
                                  onClick={() => openLightbox(photo)}
                                  className="w-14 h-14 object-cover rounded-xl cursor-pointer ring-2 ring-transparent hover:ring-[#3A5C8F] transition-all"
                                />
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deletePhoto(album.album_id, photoIndex);
                                  }}
                                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                            {album.photos.length > 6 && (
                              <div 
                                onClick={() => album.photos.length > 0 && openLightbox(album.photos[0])}
                                className="w-14 h-14 bg-gradient-to-br from-[#EBF0F5] to-[#F2E9D9] rounded-xl flex items-center justify-center cursor-pointer hover:from-[#D7E0EA] hover:to-[#EAD9BF] transition-colors"
                              >
                                <span className="text-xs text-[#2B4C7E] font-bold">+{album.photos.length - 6}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Video Thumbnails */}
                      {album.videos && album.videos.length > 0 && (
                        <div className="mt-4">
                          <p className="text-xs text-gray-400 mb-2 font-medium">Videos</p>
                          <div className="flex flex-wrap gap-2">
                            {album.videos.slice(0, 4).map((video, videoIndex) => (
                              <div key={videoIndex} className="relative group">
                                <div className="w-20 h-14 bg-gradient-to-br from-[#F2E9D9] to-[#F2E9D9] rounded-xl overflow-hidden cursor-pointer ring-2 ring-transparent hover:ring-[#D0B384] transition-all">
                                  <video
                                    src={video}
                                    className="w-full h-full object-cover"
                                    onClick={() => setPlayingMedia({ type: 'video', src: video })}
                                  />
                                  <div 
                                    className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors"
                                    onClick={() => setPlayingMedia({ type: 'video', src: video })}
                                  >
                                    <Play className="h-5 w-5 text-white fill-white" />
                                  </div>
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteVideo(album.album_id, videoIndex);
                                  }}
                                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Audio Recordings */}
                      {album.audio_recordings && album.audio_recordings.length > 0 && (
                        <div className="mt-4">
                          <p className="text-xs text-gray-400 mb-2 font-medium">Voice Messages</p>
                          <div className="space-y-2">
                            {album.audio_recordings.slice(0, 3).map((audio, audioIndex) => (
                              <div key={audioIndex} className="relative group flex items-center gap-2 p-2 bg-gradient-to-r from-[#EBF0F5] to-[#EBF0F5] rounded-xl">
                                <button
                                  onClick={() => setPlayingMedia({ type: 'audio', src: audio })}
                                  className="w-8 h-8 bg-gradient-to-br from-[#2B4C7E] to-[#2B4C7E] rounded-full flex items-center justify-center text-white shadow-md hover:shadow-lg transition-all"
                                >
                                  <Play className="h-4 w-4 fill-white ml-0.5" />
                                </button>
                                <div className="flex-1 h-2 bg-[#EBF0F5] rounded-full overflow-hidden">
                                  <div className="w-1/3 h-full bg-gradient-to-r from-[#3A5C8F] to-[#3A5C8F] rounded-full" />
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteAudio(album.album_id, audioIndex);
                                  }}
                                  className="w-6 h-6 bg-red-100 hover:bg-red-200 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
                                >
                                  <Trash2 className="h-3 w-3 text-red-500" />
                                </button>
                              </div>
                            ))}
                            {album.audio_recordings.length > 3 && (
                              <p className="text-xs text-[#2B4C7E] font-medium">+{album.audio_recordings.length - 3} more recordings</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Canva Design Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8"
        >
          <Card className="p-6 rounded-2xl border-2 border-[#EAD9BF] bg-gradient-to-br from-[#F7F1E8] via-[#EBF0F5] to-[#EBF0F5] shadow-lg overflow-hidden relative">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#DCC39A]/20 to-[#B9C7D6]/20 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-br from-[#B9C7D6]/20 to-[#B9C7D6]/20 rounded-full translate-y-1/2 -translate-x-1/2" />
            
            <div className="relative z-10">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-14 h-14 bg-gradient-to-br from-[#C8A97E] via-[#2B4C7E] to-[#2B4C7E] rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                  <Palette className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-1">Create Stunning Photo & Video Content</h3>
                  <p className="text-sm text-gray-600">
                    Use Canva&apos;s free tools to edit photos, create collages, and design beautiful memory books
                  </p>
                </div>
              </div>
              
              <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 mb-4">
                <ul className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-[#F2E9D9] text-[#B3956B] flex items-center justify-center text-xs font-bold">✓</span>
                    <span>Photo editing & filters</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-[#EBF0F5] text-[#2B4C7E] flex items-center justify-center text-xs font-bold">✓</span>
                    <span>Collage templates</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-[#EBF0F5] text-[#2B4C7E] flex items-center justify-center text-xs font-bold">✓</span>
                    <span>Video montages</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs font-bold">✓</span>
                    <span>Memory book designs</span>
                  </li>
                </ul>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <a 
                  href="https://www.canva.com/photo-editor/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex-1"
                >
                  <Button className="w-full bg-gradient-to-r from-[#C8A97E] to-[#C8A97E] text-white rounded-full py-5 hover:opacity-90 shadow-lg group">
                    <Camera className="h-5 w-5 mr-2" />
                    Photo Editor
                    <ExternalLink className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </a>
                <a 
                  href="https://www.canva.com/video-editor/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex-1"
                >
                  <Button className="w-full bg-gradient-to-r from-[#2B4C7E] to-[#2B4C7E] text-white rounded-full py-5 hover:opacity-90 shadow-lg group">
                    <Video className="h-5 w-5 mr-2" />
                    Video Editor
                    <ExternalLink className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </a>
              </div>
              
              <p className="text-xs text-gray-500 text-center mt-3">
                Free to use • Download edited content and upload to your albums
              </p>
            </div>
          </Card>
        </motion.div>
      </main>

      {/* Create Album Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-md bg-white border-2 border-[#D7E0EA] rounded-3xl shadow-2xl">
          <DialogHeader className="text-center pb-4">
            <div className="flex justify-center mb-3">
              <div className="w-16 h-16 bg-gradient-to-br from-[#EBF0F5] to-[#F2E9D9] rounded-full flex items-center justify-center">
                <FolderHeart className="h-8 w-8 text-[#2B4C7E]" />
              </div>
            </div>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              Create New Album
            </DialogTitle>
            <p className="text-gray-600 mt-2">
              Albums help you organize precious memories for your loved ones
            </p>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div>
              <label className="block text-base font-semibold text-gray-800 mb-3">
                Album Name
              </label>
              <Input
                placeholder="e.g., Family Memories, Trip to Hawaii..."
                value={newAlbum.title}
                onChange={(e) => setNewAlbum({ ...newAlbum, title: e.target.value })}
                className="h-14 text-lg rounded-xl border-2 border-[#D7E0EA] focus:border-[#2B4C7E] focus:ring-[#2B4C7E] px-4"
                data-testid="album-title-input"
              />
              <p className="text-sm text-gray-500 mt-2">
                Choose a name that describes the memories you'll store
              </p>
            </div>
          </div>
          
          <DialogFooter className="pt-4">
            <Button 
              onClick={createAlbum} 
              disabled={isCreating || !newAlbum.title.trim()}
              className="w-full h-14 text-lg bg-gradient-to-r from-[#2B4C7E] to-[#C8A97E] text-white rounded-full hover:from-[#2B4C7E] hover:to-[#B3956B] shadow-lg disabled:opacity-50"
              data-testid="submit-album-btn"
            >
              {isCreating ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  className="flex items-center gap-2"
                >
                  <Sparkles className="h-5 w-5" />
                  Creating...
                </motion.div>
              ) : (
                <>
                  <FolderHeart className="h-5 w-5 mr-2" />
                  Create Album
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Lightbox for Zooming */}
      <ImageLightbox
        open={lightboxOpen}
        onClose={() => {
          setLightboxOpen(false);
          setSelectedImage(null);
        }}
        imageSrc={selectedImage}
        imageAlt="Album photo"
      />

      {/* Image Crop Modal */}
      <ImageCropModal
        open={cropModalOpen}
        onClose={() => {
          setCropModalOpen(false);
          if (imageToCrop) {
            URL.revokeObjectURL(imageToCrop);
            setImageToCrop(null);
          }
          setPendingUpload({ albumId: null, files: [] });
        }}
        imageSrc={imageToCrop}
        onCropComplete={handleCropComplete}
        aspectRatio={4/3}
      />

      {/* Media Recorder Modal */}
      <MediaRecorderModal
        open={recorderOpen}
        onClose={() => {
          setRecorderOpen(false);
          setRecordingAlbumId(null);
        }}
        onSave={handleRecordingSave}
        type={recorderType}
      />

      {/* Media Playback Dialog */}
      <Dialog open={!!playingMedia} onOpenChange={() => setPlayingMedia(null)}>
        <DialogContent className="max-w-2xl bg-gradient-to-br from-slate-900 via-[#0F1E36] to-slate-900 border-2 border-[#2B4C7E]/30 rounded-3xl p-0 overflow-hidden">
          {playingMedia?.type === 'video' && (
            <video
              src={playingMedia.src}
              controls
              autoPlay
              className="w-full aspect-video"
            />
          )}
          {playingMedia?.type === 'audio' && (
            <div className="p-8">
              <div className="flex flex-col items-center justify-center">
                <motion.div 
                  className="w-24 h-24 bg-gradient-to-br from-[#2B4C7E] to-[#2B4C7E] rounded-full flex items-center justify-center mb-6"
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  <Mic className="h-12 w-12 text-white" />
                </motion.div>
                <audio
                  src={playingMedia.src}
                  controls
                  autoPlay
                  className="w-full"
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Album Confirmation */}
      <SimsConfirmDialog
        open={deleteAlbumDialog}
        onClose={() => {
          setDeleteAlbumDialog(false);
          setAlbumToDelete(null);
        }}
        onConfirm={deleteAlbum}
        title="Delete Album?"
        message={`Are you sure you want to delete "${albumToDelete?.title}"? All photos, videos, and voice recordings in this album will be permanently removed.`}
        confirmText="Yes, Delete"
        cancelText="Keep It"
        variant="error"
      />
    </div>
  );
}
